import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Download, Loader2, FileWarning } from "lucide-react";

const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"];

const extOf = (name = "") => (name.split(".").pop() || "").toLowerCase();

/** Can we show this in the browser, or is downloading the only option? */
export const canPreview = (filename, contentType = "") => {
  const t = (contentType || "").toLowerCase();
  const e = extOf(filename);
  return t.startsWith("image/") || t === "application/pdf" || IMAGE_EXT.includes(e) || e === "pdf";
};

const isPdf = (filename, contentType = "") =>
  (contentType || "").toLowerCase() === "application/pdf" || extOf(filename) === "pdf";

/**
 * FileLightbox — read the whole document without downloading it. Images open at full
 * resolution; PDFs render in the browser's own viewer, so a multi-page bank statement is
 * scrollable and readable in place. Escape or the backdrop closes it.
 */
export const FileLightbox = ({ fileId, filename, contentType, onClose }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const pdf = isPdf(filename, contentType);

  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    api.get(`/hub/files/${fileId}`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: contentType || r.data.type });
        objectUrl = URL.createObjectURL(blob);
        if (alive) setUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fileId, contentType]);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [close]);

  const download = () => {
    if (!url) return;
    const a = document.createElement("a"); a.href = url; a.download = filename || "document";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div onClick={close} data-testid="file-lightbox"
         className="fixed inset-0 z-[160] bg-[#0B1220]/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-150">
      {/* Bar */}
      <div onClick={(e) => e.stopPropagation()}
           className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 h-16 border-b border-white/10">
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-white truncate">{filename}</div>
          <div className="text-[11px] text-slate-400">Press Escape or tap outside to close</div>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={download} disabled={!url}
                  className="rounded-full h-9 text-[12.5px] bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
                  data-testid="file-lightbox-download">
            <Download className="size-3.5 mr-1.5" /> Download
          </Button>
          <button onClick={close} className="grid place-items-center size-9 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors"
                  data-testid="file-lightbox-close">
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div onClick={(e) => e.stopPropagation()} className="flex-1 min-h-0 p-3 sm:p-6">
        {failed ? (
          <div className="h-full grid place-items-center text-center">
            <div>
              <FileWarning className="size-6 text-slate-500 mx-auto" />
              <div className="mt-2 text-[13.5px] text-slate-300">We couldn't open that one.</div>
              <div className="mt-1 text-[12px] text-slate-500">Try downloading it instead.</div>
            </div>
          </div>
        ) : !url ? (
          <div className="h-full grid place-items-center"><Loader2 className="size-6 animate-spin text-[#7da9ff]" /></div>
        ) : pdf ? (
          <iframe src={url} title={filename} className="w-full h-full rounded-xl bg-white border border-white/10"
                  data-testid="file-lightbox-pdf" />
        ) : (
          <div className="h-full flex items-center justify-center overflow-auto">
            <img src={url} alt={filename} className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
                 data-testid="file-lightbox-image" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * useLightbox — one shared hook so every file row behaves the same: previewable files open,
 * everything else downloads.
 */
export const useLightbox = () => {
  const [open, setOpen] = useState(null);

  const openOrDownload = async (file) => {
    const { id, filename, content_type } = file;
    if (canPreview(filename, content_type)) { setOpen({ id, filename, content_type }); return; }
    try {
      const r = await api.get(`/hub/files/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: content_type || r.data.type }));
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed — please try again"); }
  };

  const node = open ? (
    <FileLightbox fileId={open.id} filename={open.filename} contentType={open.content_type}
                  onClose={() => setOpen(null)} />
  ) : null;

  return { openOrDownload, lightbox: node };
};
